import { AppDispatch, GetState, RootState } from '../store';
import { selectPersonaEmail, selectPersonaInformation } from '../slices/persona.slice';
import { selectValidationJWT } from '../slices/session.slice';
import { changeStep, Step } from '../slices/step.slice';
import { selectFeature, selectFeatureSettings } from '../slices/features.slice';
import { AppFeature, ComplexFeatureSettings } from '../../types/check/flags';
import { z, ZodObject } from 'zod';

/**
 *	Initialize current step based on application state (redux store).
 * */
export const initializeCurrentStep = () => {
	return (dispatch: AppDispatch, getState: GetState) => {
		const state = getState();
		const reportStatus = state.reports.reportStatus;

		switch (reportStatus) {
			case 'NO_EMAIL':
				dispatch(initializeEmailInputStep());
				break;
			case 'EMAIL_VALIDATION_PENDING':
				dispatch(initializeEmailVerificationPendingStep());
				break;
			case 'PENDING':
				dispatch(initializePendingStep());
				break;
			case 'PROCESSING':
				dispatch(initializeProcessingStep());
				break;
			case 'READY':
				dispatch(initializeReadyStep());
				break;
			case 'ACCEPTED':
			case 'DISCARDED':
			case 'CANCELLED':
				dispatch(initializeFinalStep());
				break;
			case 'NO_PHONE_NUMBER':
				dispatch(initializePhoneNumberInputStep());
				break;
			case 'PHONE_NUMBER_PROVIDED':
				dispatch(initializePhoneNumberSmsDeliveryPendingStep());
				break;
			case 'PHONE_NUMBER_VALIDATION_PENDING':
				dispatch(initializePhoneNumberVerificationStep());
				break;
			case 'PHONE_NUMBER_SMS_DELIVERY_ERROR':
				dispatch(initializePhoneNumberSmsDeliveryErrorStep());
				break;
			default:
				throw new Error('Unsupported report status: ' + reportStatus);
		}
	};
};

export function initializeEmailInputStep() {
	return (dispatch: AppDispatch, getState: GetState) => {
		const state = getState();
		const email = selectPersonaEmail(state);
		if (email) {
			dispatch(changeStep('EMAIL_VERIFICATION_PENDING_STEP'));
		} else {
			dispatch(changeStep('EMAIL_INPUT_STEP'));
		}
	};
}

export function initializeEmailVerificationPendingStep() {
	return (dispatch: AppDispatch) => {
		dispatch(changeStep('EMAIL_VERIFICATION_PENDING_STEP'));
	};
}

export function initializePendingStep() {
	return (dispatch: AppDispatch, getState: GetState) => {
		const state = getState();
		const validationJWT = selectValidationJWT(state);

		if (validationJWT !== '') {
			dispatch(changeStep('EMAIL_VERIFIED_STEP'));
			return;
		}

		const nextStep = getNextStepBasedOnPersonaInformation(state);
		dispatch(changeStep(nextStep));
	};
}

const getNextStepBasedOnPersonaInformation = (state: RootState): Step => {
	const isPersonaInformationProvided = isPersonaInformationPrefilled(state);
	return isPersonaInformationProvided ? 'PERSONA_CONSENT_INPUT_STEP' : 'PERSONA_INFORMATION_INPUT_STEP';
};

export function initializeProcessingStep() {
	return (dispatch: AppDispatch, getState: GetState) => {
		const state = getState();
		const bankProcessFinished = state.ui.bankProcessFinished;

		if (bankProcessFinished) {
			dispatch(changeStep('WAITING_CHECK_RESULTS_STEP'));
		} else {
			dispatch(changeStep('BANK_STEP'));
		}
	};
}

export function initializeFinalStep() {
	return (dispatch: AppDispatch) => {
		dispatch(changeStep('CHECK_FINISHED_STEP'));
	};
}

export function initializePhoneNumberInputStep() {
	return (dispatch: AppDispatch, getState: GetState) => {
		const state = getState();
		const validationJWT = selectValidationJWT(state);

		if (validationJWT) {
			dispatch(changeStep('EMAIL_VERIFIED_STEP'));
		} else {
			dispatch(changeStep('PHONE_NUMBER_INPUT_STEP'));
		}
	};
}

export function initializePhoneNumberVerificationStep() {
	return (dispatch: AppDispatch) => {
		dispatch(changeStep('PHONE_NUMBER_VERIFICATION_CODE_INPUT_STEP'));
	};
}

export function initializePhoneNumberSmsDeliveryPendingStep() {
	return (dispatch: AppDispatch) => {
		dispatch(changeStep('PHONE_NUMBER_SMS_DELIVERY_PENDING_STEP'));
	};
}

export function initializePhoneNumberSmsDeliveryErrorStep() {
	return (dispatch: AppDispatch) => {
		dispatch(changeStep('PHONE_NUMBER_SMS_DELIVERY_ERROR_STEP'));
	};
}

export function initializeReadyStep() {
	return (dispatch: AppDispatch, getState: GetState) => {
		const state = getState();
		const isAssessmentFeatureActive = selectFeature(state, AppFeature.ASSESSMENT_USER_INPUT);
		const nextStep: Step = isAssessmentFeatureActive ? 'CHECK_RESULTS_STEP' : 'CHECK_RESULTS_SUMMARY_STEP';
		dispatch(changeStep(nextStep));
	};
}

export const isPersonaInformationPrefilled = (state: RootState): boolean => {
	const persona = selectPersonaInformation(state);
	const dateOfBirthFeatureSettings = selectFeatureSettings(state, AppFeature.DOB);
	const placeOfResidenceFeatureSettings = selectFeatureSettings(state, AppFeature.PLACE_OF_RESIDENCE);

	const validationSchema = getValidationSchema(dateOfBirthFeatureSettings, placeOfResidenceFeatureSettings);
	const { success } = validationSchema.safeParse(persona);
	return success;
};

const getValidationSchema = (
	dobSettings: ComplexFeatureSettings<AppFeature.DOB> | undefined,
	residenceSettings: ComplexFeatureSettings<AppFeature.PLACE_OF_RESIDENCE> | undefined
): ZodObject<any> => {
	let schema: ZodObject<any> = personaInformationPrefillSchema;

	if (dobSettings && dobSettings.hidden) {
		schema = schema.omit({ dateOfBirth: true });
	}

	if (residenceSettings && residenceSettings.hidden) {
		schema = schema.omit({ country: true });
	}

	return schema;
};

export const personaInformationPrefillSchema = z.object({
	firstName: z.string().min(1),
	lastName: z.string().min(1),
	country: z.string().min(1),
	dateOfBirth: z.string().min(1)
});
